<template>
  <ion-page>
    <AdminHeaderMenu name="AdminHeaderMenu" />
    <ion-content class="ion-padding">
      <div class="print-container">
        <h1 class="page-title">Thermal Printer Test</h1>
        <p class="subtitle">BLD Print Service Demo</p>

        <!-- Status Card -->
        <ion-card class="status-card">
          <ion-card-header>
            <ion-card-title>Printer Status</ion-card-title>
          </ion-card-header>
          <ion-card-content>
            <div class="status-item">
              <span class="status-label">Service:</span>
              <ion-badge :color="printerStatus.bound ? 'success' : 'danger'">
                {{ printerStatus.bound ? "Connected" : "Disconnected" }}
              </ion-badge>
            </div>
            <div class="status-item">
              <span class="status-label">Printer:</span>
              <ion-badge :color="printerStatus.ready ? 'success' : 'warning'">
                {{ printerStatus.ready ? "Ready" : "Not Ready" }}
              </ion-badge>
            </div>
          </ion-card-content>
        </ion-card>

        <!-- Quick Test -->
        <ion-card>
          <ion-card-header>
            <ion-card-title>Quick Print Test</ion-card-title>
          </ion-card-header>
          <ion-card-content>
            <ion-button
              expand="block"
              @click="printDemo('OK')"
              :disabled="!printerStatus.ready || printing"
            >
              <ion-spinner v-if="printing" name="crescent"></ion-spinner>
              <span v-else>Print "OK"</span>
            </ion-button>

            <ion-button
              expand="block"
              @click="printDemo('HELLO')"
              :disabled="!printerStatus.ready || printing"
              color="secondary"
            >
              <ion-spinner v-if="printing" name="crescent"></ion-spinner>
              <span v-else>Print "HELLO"</span>
            </ion-button>

            <ion-button
              expand="block"
              @click="printDemo('TEST')"
              :disabled="!printerStatus.ready || printing"
              color="tertiary"
            >
              <ion-spinner v-if="printing" name="crescent"></ion-spinner>
              <span v-else>Print "TEST"</span>
            </ion-button>
          </ion-card-content>
        </ion-card>

        <!-- Custom Text -->
        <ion-card>
          <ion-card-header>
            <ion-card-title>Custom Text</ion-card-title>
          </ion-card-header>
          <ion-card-content>
            <ion-input
              v-model="customText"
              placeholder="Enter text to print"
              class="custom-input"
            ></ion-input>
            <ion-button
              expand="block"
              @click="printCustom"
              :disabled="!printerStatus.ready || !customText || printing"
            >
              <ion-spinner v-if="printing" name="crescent"></ion-spinner>
              <span v-else>Print Custom Text</span>
            </ion-button>
          </ion-card-content>
        </ion-card>

        <!-- Last Result -->
        <ion-card
          v-if="lastResult"
          :class="lastResult.success ? 'result-success' : 'result-error'"
        >
          <ion-card-header>
            <ion-card-title>
              {{ lastResult.success ? "✓ Success" : "✗ Error" }}
            </ion-card-title>
          </ion-card-header>
          <ion-card-content>
            {{ lastResult.message }}
          </ion-card-content>
        </ion-card>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { defineComponent } from "vue";
import AdminHeaderMenu from "@/components/admin/AdminHeaderMenu.vue";
import ThermalPrinter from "@/plugins/thermal-printer";

import {
  IonPage,
  IonContent,
  IonButton,
  IonInput,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonBadge,
  IonSpinner,
} from "@ionic/vue";

export default defineComponent({
  name: "PrintPage",
  components: {
    AdminHeaderMenu,
    IonPage,
    IonContent,
    IonButton,
    IonInput,
    IonCard,
    IonCardHeader,
    IonCardTitle,
    IonCardContent,
    IonBadge,
    IonSpinner,
  },
  data() {
    return {
      printerStatus: {
        ready: false,
        bound: false,
      },
      customText: "",
      printing: false,
      lastResult: null,
    };
  },
  mounted() {
    this.checkPrinterStatus();
  },
  methods: {
    async checkPrinterStatus() {
      try {
        const result = await ThermalPrinter.isReady();
        this.printerStatus = {
          ready: result.ready,
          bound: result.bound,
        };
        console.log("Printer status:", this.printerStatus);
      } catch (error) {
        console.error("Failed to check printer status:", error);
        this.printerStatus = {
          ready: false,
          bound: false,
        };
      }
    },

    async printDemo(text) {
      this.printing = true;
      this.lastResult = null;

      try {
        console.log("Printing demo text:", text);
        const result = await ThermalPrinter.printDemo({ text });

        this.lastResult = {
          success: true,
          message: result.message || `Successfully printed: ${text}`,
        };

        console.log("Print success:", result);
      } catch (error) {
        this.lastResult = {
          success: false,
          message: error.message || "Print failed",
        };
        console.error("Print error:", error);
      } finally {
        this.printing = false;
      }
    },

    async printCustom() {
      if (!this.customText) return;

      this.printing = true;
      this.lastResult = null;

      try {
        console.log("Printing custom text:", this.customText);
        const result = await ThermalPrinter.print({
          text: this.customText,
          fontSize: 24,
          bold: false,
          alignment: "CENTER",
        });

        this.lastResult = {
          success: true,
          message: `Successfully printed: ${this.customText}`,
        };

        console.log("Print success:", result);
        this.customText = ""; // Clear input after successful print
      } catch (error) {
        this.lastResult = {
          success: false,
          message: error.message || "Print failed",
        };
        console.error("Print error:", error);
      } finally {
        this.printing = false;
      }
    },
  },
});
</script>

<style scoped>
.print-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
}

.page-title {
  font-family: "Gilroy", sans-serif;
  font-weight: 600;
  font-size: 28px;
  color: #1b1b1b;
  margin-bottom: 8px;
}

.subtitle {
  font-family: "Gilroy", sans-serif;
  font-size: 14px;
  color: #828282;
  margin-bottom: 24px;
}

.status-card {
  margin-bottom: 20px;
}

.status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.status-label {
  font-family: "Gilroy", sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #1b1b1b;
}

.custom-input {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
  font-family: "Gilroy", sans-serif;
}

ion-button {
  --border-radius: 8px;
  font-family: "Gilroy", sans-serif;
  font-weight: 600;
  font-size: 14px;
  margin-top: 8px;
  text-transform: none;
  height: 48px;
}

ion-card {
  margin-bottom: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

ion-card-title {
  font-family: "Gilroy", sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #1b1b1b;
}

ion-card-content {
  font-family: "Gilroy", sans-serif;
  font-size: 14px;
  color: #4f4f4f;
}

.result-success {
  background-color: #e8f5e9;
  border: 2px solid #4caf50;
}

.result-error {
  background-color: #ffebee;
  border: 2px solid #f44336;
}

.result-success ion-card-title,
.result-error ion-card-title {
  font-size: 16px;
}

ion-badge {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
}

ion-spinner {
  width: 20px;
  height: 20px;
}
</style>
